import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { filter } from 'rxjs/operators';

import { EditUserVm } from 'app/shared/generated/Administration/Models/EditUserVm';
import { SystemMessageService, SystemMessage } from 'app/core/system-message/system-message-service';
import { NavigationService } from 'app/shared/navigation/navigation.service';
import { SecurityService } from 'app/core/security/security.service';
import { UserChangePasswordModalComponent } from './user-change-password-modal/user-change-password-modal.component';
import { CheckNetworkService } from 'local-db/services/check-network.service';
import { AppDatabase } from 'local-db/services/init.ldb.service';
import { SetLocaldbTablesService } from 'app/services/service-workers/set-localdb-tables.service';
import { PermissionConst } from 'app/core/security/generated/Permission';

@Component({
    selector: 'pcg-edit-user',
    templateUrl: './edit-user.component.html',
    styleUrls: ['./edit-user.component.scss'],
    standalone: false
})
export class EditUserComponent implements OnInit {

	id: number;
	isLockedOut = false;
	
	// Custom Field variables
	formGroup = EditUserVm.Form;
	
	userNameValidation = { pattern: "Only letters, numbers, and symbols ( . @ - _ ' ) are allowed" };

	// Security variables
	canEdit = false;
	canCreate = false;
	canViewLoginHistory = false;
	canUnlock = false;
	canResetPassword = false;
	canSendWelcomeEmail = false;
	canViewReports = false;
	canSeePasswordButtons = false;
	isCurrentUser = false;
	isOnline: boolean;

	openWindow = o => { this.router.navigateByUrl('/' + o); }

	constructor(
		private route: ActivatedRoute
		, private httpClient: HttpClient
		, public router: Router
		, private modalService: NgbModal
		, private ms: SystemMessageService
		, private nav: NavigationService
		, title: Title
		, private sec: SecurityService
		, private localDb: AppDatabase
		, private localDbService: SetLocaldbTablesService
	) { title.setTitle('Edit User'); }

	ngOnInit() {
		this.isOnline = CheckNetworkService.checkNetworkStatus();
		this.addSecuritySubscription();
		this.addRouteSubscription();
	}

	/** Update the local dynamic security object when the security updates */
	addSecuritySubscription() {
		this.sec.security$.subscribe((sec: PermissionConst) => {
			this.sec.setControlAccess(this.formGroup?.controls?.isActive, o => o.manageUsers);
			this.canEdit = this.sec.hasAccess(o => o.manageUsers);
			this.canCreate = this.sec.hasAccess(o => o.manageUsers);
			this.canViewLoginHistory = this.sec.hasAccess(o => o.manageUsers);
			this.canUnlock = this.sec.hasAccess(o => o.manageUsers);
			this.canResetPassword = this.sec.hasAccess(o => o.manageUsers);
			this.canSendWelcomeEmail = this.sec.hasAccess(o => o.manageUsers);
			this.canViewReports = this.sec.hasAccess(o => o.manageUsers);
		});
	}

	/** Subscribe to route to pick up id changes and update page accordingly */
	addRouteSubscription() {
		this.route.paramMap.subscribe(map => {
			// Allow user to view page if it is either their profile
			// or they have admin access to view other people's profiles
			const user = this.sec.getUser();
			this.id = parseInt(map.get('id'), 10);
			if (
				(
					this.id !== user?.id 
					&& !this.canEdit
				) 
				|| (
					this.id === 0 
					&& !this.canCreate
				)
			) { this.router.navigate(['/']); }

			this.isCurrentUser = this.id === this.sec.getUser()?.id;
			if (this.isOnline) {
				this.httpClient.get(`api/Administration/Users/GetUser/${this.id}`)
					.subscribe((model: EditUserVm) => {
						this.formGroup.patchValue(model);
						this.canSeePasswordButtons = model.isActive 
							&& !model.isLockedOut 
							&& model.userId !== this.sec.getUser().id 
							&& model.userId !== 0;
						this.isLockedOut = model.isLockedOut;
					}
				);
			} else { this.grabUserFromLocalDb(); }
			
			// I'm both setting the secondary nav in ngOnInit and on nav change
			// since the secondary nav was otherwise not changing when the username
			// in the corner was clicked while on this page
			this.setSecondaryNav();
			this.router.events.pipe(filter(event => event instanceof NavigationEnd))
				.subscribe((event: NavigationEnd) => { this.setSecondaryNav(); });
		});
	}

	/** Set the navigation that sits under the main navigation */
	setSecondaryNav() {
		if (this.sec.hasAccess(o => o.manageUsers)) { this.nav.setOpenSecondaryMenu(['Admin', 'Users', 'User List']); }
	}

	/** The main save event for the page */
	saveUser(msg: SystemMessage) {
		if (msg.value) {
			const model = this.formGroup.value;
			this.id = msg.model?.userId;
			this.canSeePasswordButtons = model.isActive 
				&& !model.isLockedOut 
				&& model.userId !== this.sec.getUser().id 
				&& model.userId !== 0;
			this.sec.setSecurity(
				undefined
				, msg?.value?.userJsVm
				, msg?.value?.permissions
			);
			if (this.isOnline) { this.localDbService.setLocalDbTable("EditUserTbl", this.formGroup.value, false); } 
			else { this.localDbService.setLocalDbTable("EditUserTbl", this.formGroup.value, true); }
		}
	}

	/** Unlock user, called when the unlock svg clicked on locked user */
	unlockUser() { this.ms.getHttpObservable(this, `api/Administration/Users/UnlockUser/${this.id}`).subscribe(() => { this.isLockedOut = false; }); }

	/** Called on click of the reset password and welcome email buttons */
	resetPassword(isWelcomeEmail: boolean) {
		this.httpClient.post(
			'api/Account/ForgotPassword'
			, {
				email: this.formGroup.value.email
				, isWelcomeEmail
			}).subscribe((msg: SystemMessage) => {
				if (msg.isSuccessful) {
					this.ms.setSystemMessage(`An email has been sent to the user with a ${isWelcomeEmail ? 'welcome' : 'reset password'} link.`);
				} else {
					this.ms.setSystemMessage('An error occurred when attempting to send the email.', 'error');
				}
			}
		);
	}

	/** Opens the change password modal.
	 * Called on click of the Change Passoword button when user looking at their profile. */
	openChangePasswordModal() { this.modalService.open(UserChangePasswordModalComponent); }

	grabUserFromLocalDb() {
		// get data from localDb if offline and there is something in the localDb
		this.localDb.table("EditUserTbl").toArray()
			.then((response) => { 
				if (response.length > 0) {
					let filteredResponse = response.filter(o => o.userId === this.id && this.id !== 0);
					if (filteredResponse.length > 0) { 
						this.formGroup.patchValue(filteredResponse[filteredResponse.length - 1]);
					} else { // means a new user is being created offline
						this.formGroup.controls.isActive.setValue(true);
					}	
				}
			});
	}

	activateDeactivate(id, value) {
		this.httpClient.get(`api/Administration/Users/ActivateDeactivate?id=${id}&isActive=${value}`).subscribe((message: SystemMessage)=>{
			this.ms.setSystemMessage(message.message, message.messageClass);
		});
	}
}
